import React, { useState } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Pressable,
  Linking,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AntDesign, Feather, Ionicons } from '@expo/vector-icons';
import { fetchGroupById } from '../../services/groupService';
import { fetchPostsByGroupId } from '../../services/postService';
import { useTheme } from '../../contexts/ThemeContext';
import { ZONE_TYPE_INFO, Post } from '../../types';
import CachedImage from '../../components/CachedImage';
import PostListItem from '../../components/PostListItem';
import PixelContainer from '../../components/PixelContainer';
import { fontStyle } from '../../config/fonts';

type Tab = 'posts' | 'about';

export default function ZoneDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const router = useRouter();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('posts');

  // Fetch zone
  const {
    data: zone,
    isLoading: isLoadingZone,
    isError: isZoneError,
  } = useQuery({
    queryKey: ['group', id],
    queryFn: () => fetchGroupById(id!),
    enabled: !!id,
  });

  // Fetch posts
  const {
    data: postsData,
    isLoading: isLoadingPosts,
  } = useQuery({
    queryKey: ['zone-posts', id],
    queryFn: () => fetchPostsByGroupId(id!),
    enabled: !!id,
  });

  const typeInfo = zone ? ZONE_TYPE_INFO[zone.type] : null;

  const handleOpenUrl = (url: string) => {
    Linking.openURL(url);
  };

  const renderHeader = () => {
    if (!zone) return null;

    return (
      <View>
        {/* Banner / Header */}
        <View style={[styles.header, { backgroundColor: typeInfo?.color || colors.primary }]}>
          {zone.banner ? (
            <CachedImage uri={zone.banner} style={styles.banner} />
          ) : (
            <View style={[styles.bannerPlaceholder, { backgroundColor: typeInfo?.color || colors.primary }]} />
          )}

          {/* Back Button */}
          <Pressable
            style={[styles.backButton, { backgroundColor: 'rgba(0,0,0,0.3)' }]}
            onPress={() => router.back()}
          >
            <Feather name="arrow-left" size={22} color="#fff" />
          </Pressable>
        </View>

        {/* Zone Info */}
        <View style={[styles.zoneInfo, { backgroundColor: colors.background }]}>
          {/* Zone Image */}
          <View style={styles.zoneImageWrapper}>
            {zone.image ? (
              <CachedImage uri={zone.image} style={styles.zoneImage} />
            ) : (
              <View style={[styles.zoneImage, styles.zoneImagePlaceholder, { backgroundColor: colors.surface }]}>
                <Feather name="users" size={32} color={colors.textSecondary} />
              </View>
            )}
            <View style={[styles.typeBadgeLarge, { backgroundColor: typeInfo?.color }]}>
              <Text style={styles.typeBadgeEmoji}>{typeInfo?.emoji}</Text>
            </View>
          </View>

          {/* Zone Name & Type */}
          <Text style={[styles.zoneName, fontStyle, { color: colors.text }]}>{zone.name}</Text>
          <View style={styles.typeRow}>
            <Text style={[styles.zoneType, fontStyle, { color: typeInfo?.color }]}>
              {t(`zone.types.${zone.type}`)}
            </Text>
            <Text style={[styles.memberCount, fontStyle, { color: colors.textSecondary }]}>
              {zone.membersCount || 0} {(zone.membersCount || 0) === 1 ? t('zone.member') : t('zone.members')}
            </Text>
          </View>

          {/* Topics */}
          {zone.topics && zone.topics.length > 0 && (
            <View style={styles.topicsRow}>
              {zone.topics.map((topic) => (
                <View key={topic} style={[styles.topicTag, { backgroundColor: colors.surface }]}>
                  <Text style={[styles.topicText, fontStyle, { color: colors.textSecondary }]}>
                    {t(`zone.topics.${topic}`)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Join Button */}
          <Pressable>
            <PixelContainer backgroundColor={colors.primary} cornerSize={5} style={styles.joinButton}>
              <Text style={[styles.joinButtonText, fontStyle]}>{t('common.join')}</Text>
            </PixelContainer>
          </Pressable>
        </View>

        {/* Tabs */}
        <View style={[styles.tabsContainer, { borderBottomColor: colors.border }]}>
          <Pressable
            style={[styles.tab, activeTab === 'posts' && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab('posts')}
          >
            <Text
              style={[
                styles.tabText,
                fontStyle,
                { color: activeTab === 'posts' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('zone.posts')}
            </Text>
          </Pressable>
          <Pressable
            style={[styles.tab, activeTab === 'about' && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab('about')}
          >
            <Text
              style={[
                styles.tabText,
                fontStyle,
                { color: activeTab === 'about' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('zone.about')}
            </Text>
          </Pressable>
        </View>

        {/* About Tab Content */}
        {activeTab === 'about' && (
          <View style={styles.aboutContent}>
            {zone.description && (
              <View style={styles.aboutSection}>
                <Text style={[styles.aboutSectionTitle, fontStyle, { color: colors.text }]}>
                  {t('zone.zoneDescription')}
                </Text>
                <Text style={[styles.aboutText, fontStyle, { color: colors.textSecondary }]}>
                  {zone.description}
                </Text>
              </View>
            )}

            {/* Server-specific fields */}
            {zone.type === 'server' && zone.serverFields && (
              <View style={styles.aboutSection}>
                <Text style={[styles.aboutSectionTitle, fontStyle, { color: colors.text }]}>
                  {t('zone.serverFields.status')}
                </Text>

                {zone.serverFields.serverIp && (
                  <View style={styles.fieldRow}>
                    <Text style={[styles.fieldLabel, fontStyle, { color: colors.textSecondary }]}>
                      {t('zone.serverFields.serverIp')}:
                    </Text>
                    <Text style={[styles.fieldValue, fontStyle, { color: colors.text }]}>
                      {zone.serverFields.serverIp}
                      {zone.serverFields.serverPort && `:${zone.serverFields.serverPort}`}
                    </Text>
                  </View>
                )}

                {zone.serverFields.region && (
                  <View style={styles.fieldRow}>
                    <Text style={[styles.fieldLabel, fontStyle, { color: colors.textSecondary }]}>
                      {t('zone.serverFields.region')}:
                    </Text>
                    <Text style={[styles.fieldValue, fontStyle, { color: colors.text }]}>
                      {zone.serverFields.region.toUpperCase()}
                    </Text>
                  </View>
                )}

                <View style={styles.fieldRow}>
                  <View
                    style={[
                      styles.statusDot,
                      { backgroundColor: zone.serverFields.isOnline ? '#10B981' : '#EF4444' },
                    ]}
                  />
                  <Text style={[styles.fieldValue, fontStyle, { color: colors.text }]}>
                    {zone.serverFields.isOnline ? t('zone.online') : t('zone.offline')}
                    {zone.serverFields.isOnline &&
                      zone.serverFields.playerCount !== undefined &&
                      ` - ${zone.serverFields.playerCount}/${zone.serverFields.maxPlayers || '?'} ${t('zone.players')}`}
                  </Text>
                </View>
              </View>
            )}

            {/* Project-specific fields */}
            {zone.type === 'project' && zone.projectFields && (
              <View style={styles.aboutSection}>
                <Text style={[styles.aboutSectionTitle, fontStyle, { color: colors.text }]}>
                  Project Info
                </Text>

                {zone.projectFields.version && (
                  <View style={styles.fieldRow}>
                    <Text style={[styles.fieldLabel, fontStyle, { color: colors.textSecondary }]}>
                      {t('zone.projectFields.version')}:
                    </Text>
                    <Text style={[styles.fieldValue, fontStyle, { color: colors.text }]}>
                      v{zone.projectFields.version}
                    </Text>
                  </View>
                )}

                {zone.projectFields.compatibility && (
                  <View style={styles.fieldRow}>
                    <Text style={[styles.fieldLabel, fontStyle, { color: colors.textSecondary }]}>
                      {t('zone.projectFields.compatibility')}:
                    </Text>
                    <Text style={[styles.fieldValue, fontStyle, { color: colors.text }]}>
                      {zone.projectFields.compatibility}
                    </Text>
                  </View>
                )}

                {zone.projectFields.downloadUrl && (
                  <Pressable onPress={() => handleOpenUrl(zone.projectFields!.downloadUrl!)}>
                    <PixelContainer backgroundColor={colors.primary} cornerSize={4} style={styles.linkButton}>
                      <Feather name="download" size={16} color="#fff" />
                      <Text style={[styles.linkButtonText, fontStyle]}>{t('zone.projectFields.downloadUrl')}</Text>
                    </PixelContainer>
                  </Pressable>
                )}

                {zone.projectFields.repositoryUrl && (
                  <Pressable onPress={() => handleOpenUrl(zone.projectFields!.repositoryUrl!)}>
                    <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.linkButton}>
                      <Ionicons name="logo-github" size={16} color={colors.text} />
                      <Text style={[styles.linkButtonText, fontStyle, { color: colors.text }]}>
                        {t('zone.projectFields.repositoryUrl')}
                      </Text>
                    </PixelContainer>
                  </Pressable>
                )}
              </View>
            )}

            {/* Guild-specific fields */}
            {zone.type === 'guild' && zone.guildFields && (
              <View style={styles.aboutSection}>
                <Text style={[styles.aboutSectionTitle, fontStyle, { color: colors.text }]}>
                  Guild Info
                </Text>

                <View style={styles.fieldRow}>
                  <Text style={[styles.fieldLabel, fontStyle, { color: colors.textSecondary }]}>
                    {t('zone.guildFields.recruiting')}:
                  </Text>
                  <Text
                    style={[
                      styles.fieldValue,
                      fontStyle,
                      { color: zone.guildFields.isRecruiting ? '#10B981' : colors.textSecondary },
                    ]}
                  >
                    {zone.guildFields.isRecruiting ? t('zone.recruiting') : t('zone.notRecruiting')}
                  </Text>
                </View>

                {zone.guildFields.discordUrl && (
                  <Pressable onPress={() => handleOpenUrl(zone.guildFields!.discordUrl!)}>
                    <PixelContainer backgroundColor="#5865F2" cornerSize={4} style={styles.linkButton}>
                      <Ionicons name="logo-discord" size={16} color="#fff" />
                      <Text style={[styles.linkButtonText, fontStyle]}>{t('zone.guildFields.discordUrl')}</Text>
                    </PixelContainer>
                  </Pressable>
                )}
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderPost = ({ item }: { item: Post }) => (
    <PostListItem post={{ ...item, group: zone || undefined }} />
  );

  const renderEmpty = () => {
    if (isLoadingPosts || activeTab === 'about') return null;
    return (
      <View style={styles.emptyContainer}>
        <Feather name="file-text" size={48} color={colors.textSecondary} />
        <Text style={[styles.emptyText, fontStyle, { color: colors.textSecondary }]}>
          {t('empty.noPosts')}
        </Text>
      </View>
    );
  };

  if (isLoadingZone) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (isZoneError || !zone) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, fontStyle, { color: colors.error }]}>{t('errors.loadFailed')}</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={['bottom']}>
      <FlatList
        data={activeTab === 'posts' ? (postsData?.posts || []) : []}
        renderItem={renderPost}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
  },
  listContent: {
    paddingBottom: 20,
  },
  header: {
    height: 120,
    position: 'relative',
  },
  banner: {
    width: '100%',
    height: '100%',
  },
  bannerPlaceholder: {
    width: '100%',
    height: '100%',
  },
  backButton: {
    position: 'absolute',
    top: 12,
    left: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoneInfo: {
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 16,
    marginTop: -40,
  },
  zoneImageWrapper: {
    position: 'relative',
    marginBottom: 12,
  },
  zoneImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: '#fff',
  },
  zoneImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadgeLarge: {
    position: 'absolute',
    bottom: 0,
    right: -4,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#fff',
  },
  typeBadgeEmoji: {
    fontSize: 12,
  },
  zoneName: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  typeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  zoneType: {
    fontSize: 13,
    fontWeight: '600',
  },
  memberCount: {
    fontSize: 13,
  },
  topicsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 16,
  },
  topicTag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  topicText: {
    fontSize: 12,
  },
  joinButton: {
    paddingHorizontal: 48,
    paddingVertical: 12,
  },
  joinButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    marginTop: 8,
  },
  tab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
  },
  aboutContent: {
    padding: 16,
  },
  aboutSection: {
    marginBottom: 20,
  },
  aboutSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  aboutText: {
    fontSize: 14,
    lineHeight: 22,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 13,
  },
  fieldValue: {
    fontSize: 13,
    fontWeight: '500',
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    marginTop: 8,
  },
  linkButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    fontSize: 15,
  },
});
